// src/pages/dashboard/ArchivePage.tsx
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuditRow } from '@/components/ui/AuditRow';
import { AuditDetailPanel, type DetailPanelEntry } from '@/components/ui/AuditDetailPanel';
import { getAuditLog, latestPerBooking } from '@/lib/services/audit';
import { getSentArchive, getRejectedArchive, reingestWorkflow, getThreadReplies } from '@/lib/services/bookings';
import { SearchBar } from '@/components/ui/SearchBar';
import type { SearchFilters } from '@/components/ui/SearchBar';
import type { AuditEntry } from '@/lib/types/audit';
import type { RawEmail } from '@/lib/types/booking';
import type { BackendWorkflow } from '@/lib/types/workflow';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { WorkflowStatus } from '@/lib/types/workflow';
import { formatSAST, formatSASTShort } from '@/lib/utils/date';
import { brand } from '@/lib/tokens';
import { statusColors } from '@/lib/statusColors';
import { useAsyncList } from '@/lib/hooks/useAsyncList';
import { ListLoading, ListError, ListEmpty } from '@/components/ui/ListState';
import { useToast } from '@/context/ToastContext';

// 'quarantine' kept as an accepted (but redirected) URL value below so old
// bookmarks/links to ?tab=quarantine still land somewhere sensible after
// the Quarantine and Deleted tabs were merged into one "Rejected" tab
// (W17 D07) — see [[finding-rejected-quarantine-tabs-merged]].
type Tab = 'sent' | 'rejected' | 'audit' | 'replies';
type LegacyTab = Tab | 'deleted' | 'quarantine';

const LEGACY_TAB_REDIRECTS: Partial<Record<LegacyTab, Tab>> = {
  deleted: 'rejected',
  quarantine: 'rejected',
};

function normaliseTab(raw: string | null): Tab {
  const value = (raw as LegacyTab) ?? 'sent';
  return LEGACY_TAB_REDIRECTS[value] ?? (value as Tab);
}

function ArchiveInner() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>(normaliseTab(params.get('tab')));

  const [sentCount, setSentCount] = useState<number | null>(null);
  const [rejectedCount, setRejectedCount] = useState<number | null>(null);
  const [auditCount, setAuditCount] = useState<number | null>(null);
  const [repliesCount, setRepliesCount] = useState<number | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({ search: '', date_from: '', date_to: '' });
  const [period, setPeriod] = useState<Period>('all');
  const [order, setOrder] = useState<SortOrder>('date-desc');

  useEffect(() => {
    setTab(normaliseTab(params.get('tab')));
  }, [params]);

  function setTabAndUrl(next: Tab) {
    setTab(next);
    navigate(`/archive?tab=${next}`, { replace: true });
  }

  return (
    <div className="archive-page" style={{ padding: '24px 32px 32px', flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
      <header className="mb-6">
        <span className="mono-label">Archive</span>
        <h1
          className="font-serif mt-1"
          style={{ fontSize: 28, color: 'var(--text-primary)', letterSpacing: '-0.005em' }}
        >
          Sent quotes, rejected bookings &amp; audit trail
        </h1>
      </header>

      <SearchBar onSearch={setFilters} placeholder="Search by client email or subject..." />
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <PeriodSortBar period={period} onChange={setPeriod} />
        <SortOrderControl order={order} onChange={setOrder} />
      </div>

      {/* Tab strip */}
      <nav
        className="flex items-center gap-1 mb-6 archive-tabs"
        style={{ borderBottom: '1px solid var(--hairline)' }}
      >
        <TabButton active={tab === 'sent'} onClick={() => setTabAndUrl('sent')}>
          Sent {sentCount !== null && <Count>{sentCount}</Count>}
        </TabButton>
        <TabButton active={tab === 'rejected'} onClick={() => setTabAndUrl('rejected')}>
          Rejected {rejectedCount !== null && <Count>{rejectedCount}</Count>}
        </TabButton>
        <TabButton active={tab === 'audit'} onClick={() => setTabAndUrl('audit')}>
          Audit Log {auditCount !== null && <Count>{auditCount}</Count>}
        </TabButton>
        <TabButton active={tab === 'replies'} onClick={() => setTabAndUrl('replies')}>
          Replies {repliesCount !== null && <Count>{repliesCount}</Count>}
        </TabButton>
      </nav>

      <div className="flex-1 min-w-0 screen-fade archive-tab-content custom-scroll">
        {tab === 'sent' && <SentList onCountLoaded={setSentCount} filters={filters} period={period} order={order} />}
        {tab === 'rejected' && <RejectedList onCountLoaded={setRejectedCount} filters={filters} period={period} order={order} />}
        {tab === 'audit' && <AuditList onCountLoaded={setAuditCount} period={period} order={order} />}
        {tab === 'replies' && <RepliesList onCountLoaded={setRepliesCount} period={period} order={order} />}
      </div>
    </div>
  );
}

export default function ArchivePage() {
  return (
    <Suspense fallback={<div />}>
      <ArchiveInner />
    </Suspense>
  );
}

function TabButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="font-sans"
      style={{
        background: 'transparent',
        border: 'none',
        color: active ? 'var(--text-primary)' : brand.textFaint,
        padding: '12px 16px',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        position: 'relative',
        marginBottom: -1,
        borderBottom: active ? '2px solid var(--text-primary)' : '2px solid transparent',
        transition: 'color 150ms ease',
      }}
    >
      {children}
    </button>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="font-mono ml-2 tabular"
      style={{
        fontSize: 10.5,
        color: brand.textDim,
        padding: '2px 6px',
        borderRadius: 999,
        background: brand.surfaceSubtle,
        border: '1px solid var(--hairline)',
      }}
    >
      {children}
    </span>
  );
}

// ── Search + period sort (shared) ──────────────────────────────────────────────

type Period = 'all' | 'day' | 'week' | 'month';

const PERIOD_MS: Record<Exclude<Period, 'all'>, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

function withinPeriod(isoDate: string | null | undefined, period: Period): boolean {
  if (period === 'all') return true;
  if (!isoDate) return false;
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= PERIOD_MS[period];
}

function PeriodSortBar({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  const options: { value: Exclude<Period, 'all'>; label: string }[] = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <span className="mono-label">Sort</span>
      {options.map((opt) => {
        const active = period === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(active ? 'all' : opt.value)}
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 12,
              padding: '4px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              background: active ? brand.accent : 'transparent',
              color: active ? brand.canvas : 'var(--text-secondary)',
              border: active ? `1px solid ${brand.accent}` : '1px solid var(--hairline)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


// ── Chronological/alphabetical ordering — distinct from PeriodSortBar above
// (a recency-window filter, despite its own "Sort" label). Applied on top of
// the period filter and search results in every list below. ──────────────

type SortOrder = 'date-desc' | 'date-asc' | 'client-asc' | 'client-desc';

const SORT_ORDER_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
  { value: 'client-asc', label: 'Client A–Z' },
  { value: 'client-desc', label: 'Client Z–A' },
];

function SortOrderControl({ order, onChange }: { order: SortOrder; onChange: (o: SortOrder) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <span className="mono-label">Order</span>
      <select
        value={order}
        onChange={(e) => onChange(e.target.value as SortOrder)}
        style={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: 12,
          padding: '4px 10px',
          borderRadius: 4,
          background: 'transparent',
          color: 'var(--text-secondary)',
          border: '1px solid var(--hairline)',
        }}
      >
        {SORT_ORDER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function sortByDateAndClient<T>(
  rows: T[],
  order: SortOrder,
  getDate: (row: T) => string | null | undefined,
  getClient: (row: T) => string | null | undefined,
): T[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    if (order === 'date-desc' || order === 'date-asc') {
      const ta = new Date(getDate(a) ?? 0).getTime();
      const tb = new Date(getDate(b) ?? 0).getTime();
      const na = Number.isNaN(ta) ? 0 : ta;
      const nb = Number.isNaN(tb) ? 0 : tb;
      return order === 'date-desc' ? nb - na : na - nb;
    }
    const ca = (getClient(a) ?? '').toLowerCase();
    const cb = (getClient(b) ?? '').toLowerCase();
    const cmp = ca.localeCompare(cb);
    return order === 'client-asc' ? cmp : -cmp;
  });
  return sorted;
}

// ── Sent ──────────────────────────────────────────────────────────────────────

function SentList({ onCountLoaded, filters, period, order }: { onCountLoaded?: (n: number) => void; filters: SearchFilters; period: Period; order: SortOrder }) {
  const [selected, setSelected] = useState<DetailPanelEntry | null>(null);
  const { data: rows, loading, error } = useAsyncList<BackendWorkflow>(
    () => getSentArchive(filters).then((data) => (Array.isArray(data) ? data : (data as any).items ?? [])),
    [filters.search, filters.date_from, filters.date_to],
    'Failed to load sent archive',
    (data) => onCountLoaded?.(data.length)
  );

  const displayRows = sortByDateAndClient(
    rows.filter((r) => withinPeriod(r.sent_at, period)),
    order,
    (r) => r.sent_at,
    (r) => r.sender_email,
  );

  return (
    <>
      {loading ? (
        <ListLoading />
      ) : error ? (
        <ListError label="Failed to load sent archive" detail={error} />
      ) : displayRows.length === 0 ? (
        <ListEmpty icon="mark_email_read" label="No Sent Quotes Yet" description="Quotes you approve and send to clients will appear here." />
      ) : (
        <>
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <TH>Client</TH>
                  <TH>Subject</TH>
                  <TH width={220}>Sent At</TH>
                  <TH align="right" width={140}>Status</TH>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected({ kind: 'sent', data: r })}
                    style={{ borderTop: '1px solid var(--hairline)', cursor: 'pointer' }}
                    className="hover:bg-white/5 transition-colors duration-150"
                  >
                    <TD>
                      <span style={{ fontSize: 13 }}>
                        {r.sender_email ?? (
                          <span className="font-mono tabular" style={{ fontSize: 12 }}>
                            {r.id.slice(0, 8)}…
                          </span>
                        )}
                      </span>
                    </TD>
                    <TD>
                      <span style={{ color: brand.textSoft, fontSize: 12.5 }}>
                        {r.subject ?? r.approved_by_id ?? '—'}
                      </span>
                    </TD>
                    <TD>
                      <span className="font-mono tabular" style={{ color: brand.textSoft, fontSize: 12 }}>
                        {r.sent_at ? formatSAST(r.sent_at) : '—'}
                      </span>
                    </TD>
                    <TD align="right">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <StatusBadge status={WorkflowStatus.SENT} />
                        <HoldBadge row={r} />
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AuditDetailPanel entry={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </>
  );
}

// Hold status badge for a SENT row — informational only, no action triggered here.
function HoldBadge({ row }: { row: BackendWorkflow }) {
  if (row.hold_expired) {
    return <StatusBadge status="HOLD_EXPIRED">Hold expired</StatusBadge>;
  }
  if (row.hold_expires_at) {
    return <StatusBadge status="HOLD_ACTIVE">{`Holds until ${formatSASTShort(row.hold_expires_at)}`}</StatusBadge>;
  }
  return null;
}

// ── Audit log ─────────────────────────────────────────────────────────────────

function AuditList({ onCountLoaded, period, order }: { onCountLoaded?: (n: number) => void; period: Period; order: SortOrder }) {
  const [selected, setSelected] = useState<DetailPanelEntry | null>(null);
  // The Audit Log tab shows one row per booking — its current state —
  // rather than a row for every historical transition it passed through.
  // The backend trail itself is untouched and fully append-only; this is
  // purely a display collapse. See latestPerBooking() for detail.
  // Requested 17 Sep 2026.
  const { data: entries, loading, error } = useAsyncList<AuditEntry>(
    () => getAuditLog(200).then((data) => latestPerBooking(data)),
    [],
    'Failed to load audit log',
    (data) => onCountLoaded?.(data.length)
  );

  const displayEntries = sortByDateAndClient(
    entries.filter((e) => withinPeriod(e.timestamp, period)),
    order,
    (e) => e.timestamp,
    (e) => e.senderEmail,
  );

  return (
    <>
      {loading ? (
        <ListLoading />
      ) : error ? (
        <ListError label="Failed to load audit log" detail={error} />
      ) : displayEntries.length === 0 ? (
        <ListEmpty icon="history" label="No Audit Events Yet" description="Actions taken on quotes and bookings will be logged here." />
      ) : (
        <>
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="grid items-center px-6 py-3"
              style={{
                gridTemplateColumns: '14px 200px 280px 1fr 140px',
                gap: 16,
                borderBottom: '1px solid var(--hairline)',
              }}
            >
              <span />
              <span className="mono-label">Timestamp (SAST)</span>
              <span className="mono-label">Actor</span>
              <span className="mono-label">Action</span>
              <span className="mono-label text-right">Client</span>
            </div>
            {displayEntries.map((e) => (
              <AuditRow
                key={e.id}
                entry={e}
                onClick={() => setSelected({ kind: 'audit', data: e })}
              />
            ))}
          </div>
          <AuditDetailPanel entry={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </>
  );
}

// ── Rejected (REJECTED + QUARANTINED, merged W17 D07) ──────────────────────
// These used to be two separate tabs — Deleted (REJECTED, no way back) and
// Quarantine (QUARANTINED, re-ingestable). Both cover the same operator
// need: someone pulled a booking out of the pipeline, on purpose or after
// it failed, and another operator may want to give it another look.
// workflow-service's reingest endpoint now accepts either status (see
// [[finding-rejected-quarantine-tabs-merged]]), so this list shows both,
// merged and sorted together, with Re-ingest available on every row.

function RejectedList({ onCountLoaded, filters, period, order }: { onCountLoaded?: (n: number) => void; filters: SearchFilters; period: Period; order: SortOrder }) {
  const toast = useToast();
  const [reingesting, setReingesting] = useState<string | null>(null);
  const { data: rows, setData: setRows, loading, error } = useAsyncList<BackendWorkflow>(
    () => getRejectedArchive(filters).then((data) => (Array.isArray(data) ? data : (data as any).items ?? [])),
    [filters.search, filters.date_from, filters.date_to],
    'Failed to load rejected bookings',
    (data) => onCountLoaded?.(data.length)
  );

  async function handleReingest(id: string) {
    setReingesting(id);
    try {
      await reingestWorkflow(id);
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        onCountLoaded?.(next.length);
        return next;
      });
      toast.success('Booking re-ingested successfully');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Re-ingest failed');
    } finally {
      setReingesting(null);
    }
  }

  const displayRows = sortByDateAndClient(
    rows.filter((r) => withinPeriod(r.rejected_at ?? r.created_at, period)),
    order,
    (r) => r.rejected_at ?? r.created_at,
    (r) => r.sender_email,
  );

  return (
    <>
      {loading ? (
        <ListLoading />
      ) : error ? (
        <ListError label="Failed to load rejected bookings" detail={error} />
      ) : displayRows.length === 0 ? (
        <ListEmpty icon="delete" label="No Rejected Bookings" description="Bookings you reject, or that get quarantined after repeated failures, will appear here." />
      ) : (
        <>
          {/* archive-desktop-only: a prior fix (11 Sep 2026) kept this
              table visible on mobile with a horizontal-scroll affordance
              (archive-table-scroll) instead of letting the card's
              rounded-corner overflow:hidden clip the Action column off
              screen. That was a real improvement over clipping, but a
              live operator test showed it wasn't a real fix either — the
              button was reachable by swiping, but nothing signaled that,
              so it still read as "not there" during actual use. Replaced
              with an explicit vertical card list on mobile (below)
              instead: no gesture required, Re-ingest is always visible.
              This table now renders desktop/tablet only (see globals.css,
              .archive-desktop-only hides it below 767px). */}
          <div className="glass-card archive-table-scroll archive-desktop-only" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <TH>Client</TH>
                  <TH>Status</TH>
                  <TH width={200}>Date</TH>
                  <TH>Reason</TH>
                  <TH align="right" width={120}>Action</TH>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--hairline)' }}>
                    <TD>
                      <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>
                        {r.sender_email ?? (
                          <span className="font-mono tabular" style={{ fontSize: 12 }}>
                            {r.id.slice(0, 8)}…
                          </span>
                        )}
                      </span>
                      {r.subject && (
                        <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
                          {r.subject}
                        </div>
                      )}
                    </TD>
                    <TD>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: statusColors(r.status).color, fontSize: 12.5 }}>
                        <span
                          aria-hidden
                          style={{ width: 6, height: 6, borderRadius: 999, background: statusColors(r.status).color, flexShrink: 0 }}
                        />
                        {r.status}
                      </span>
                    </TD>
                    <TD>
                      <span className="font-mono tabular" style={{ color: brand.textSoft, fontSize: 12 }}>
                        {formatSASTShort(r.rejected_at ?? r.created_at)}
                      </span>
                    </TD>
                    <TD>
                      <span style={{ color: brand.textFaint, fontSize: 12.5 }}>
                        {r.status_reason || '—'}
                      </span>
                    </TD>
                    <TD align="right">
                      <button
                        className="btn-ghost"
                        style={{ padding: '6px 12px', fontSize: 11, opacity: reingesting === r.id ? 0.5 : 1 }}
                        disabled={reingesting === r.id}
                        onClick={() => handleReingest(r.id)}
                      >
                        {reingesting === r.id ? 'Re-ingesting…' : 'Re-ingest'}
                      </button>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* archive-mobile-cards: mobile-only vertical card list, one
              card per row, Re-ingest always full-width and on screen —
              see globals.css for the display toggle (hidden on
              desktop/tablet, shown below 767px). */}
          <div className="archive-mobile-cards">
            {displayRows.map((r) => (
              <div key={r.id} className="glass-card archive-mobile-card">
                <div className="archive-mobile-card-top">
                  <div style={{ minWidth: 0 }}>
                    <div className="archive-mobile-card-client">
                      {r.sender_email ?? (
                        <span className="font-mono tabular">{r.id.slice(0, 8)}…</span>
                      )}
                    </div>
                    {r.subject && (
                      <div className="archive-mobile-card-subject">{r.subject}</div>
                    )}
                  </div>
                  <span
                    className="archive-mobile-card-status font-sans"
                    style={{ color: statusColors(r.status).color }}
                  >
                    <span
                      aria-hidden
                      className="archive-mobile-card-dot"
                      style={{ background: statusColors(r.status).color }}
                    />
                    {r.status}
                  </span>
                </div>
                <div className="archive-mobile-card-meta">
                  <span className="font-mono tabular">
                    {formatSASTShort(r.rejected_at ?? r.created_at)}
                  </span>
                  {r.status_reason && <span>{r.status_reason}</span>}
                </div>
                <button
                  className="btn-ghost archive-mobile-card-action"
                  style={{ opacity: reingesting === r.id ? 0.5 : 1 }}
                  disabled={reingesting === r.id}
                  onClick={() => handleReingest(r.id)}
                >
                  {reingesting === r.id ? 'Re-ingesting…' : 'Re-ingest'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Replies (client replies after APPROVED/SENT, blocked from re-entering
// the pipeline by email-service's thread isolation — see
// [[finding-reply-after-send-not-blocked]]) ────────────────────────────────

function RepliesList({ onCountLoaded, period, order }: { onCountLoaded?: (n: number) => void; period: Period; order: SortOrder }) {
  const [selected, setSelected] = useState<DetailPanelEntry | null>(null);
  const { data: rows, loading, error } = useAsyncList<RawEmail>(
    () => getThreadReplies(),
    [],
    'Failed to load replies',
    (data) => onCountLoaded?.(data.length)
  );

  const displayRows = sortByDateAndClient(
    rows.filter((r) => withinPeriod(r.received_at, period)),
    order,
    (r) => r.received_at,
    (r) => r.sender_email,
  );

  return (
    <>
      {loading ? (
        <ListLoading />
      ) : error ? (
        <ListError label="Failed to load replies" detail={error} />
      ) : displayRows.length === 0 ? (
        <ListEmpty
          icon="forum"
          label="No Replies Yet"
          description="Client replies that arrive after a quote is approved or sent will appear here — they don't re-open the booking automatically."
        />
      ) : (
        <>
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <TH>Client</TH>
                  <TH>Subject</TH>
                  <TH width={220}>Received At</TH>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected({ kind: 'reply', data: r })}
                    style={{ borderTop: '1px solid var(--hairline)', cursor: 'pointer' }}
                    className="hover:bg-white/5 transition-colors duration-150"
                  >
                    <TD>
                      <span style={{ fontSize: 13 }}>{r.sender_email}</span>
                    </TD>
                    <TD>
                      <span style={{ color: brand.textSoft, fontSize: 12.5 }}>{r.subject || '—'}</span>
                    </TD>
                    <TD>
                      <span className="font-mono tabular" style={{ color: brand.textSoft, fontSize: 12 }}>
                        {formatSAST(r.received_at)}
                      </span>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AuditDetailPanel entry={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </>
  );
}

// ── Table primitives ──────────────────────────────────────────────────────────

function TH({
  children,
  align = 'left',
  width,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  width?: number;
}) {
  return (
    <th
      className="mono-label"
      style={{
        textAlign: align,
        padding: '14px 20px',
        fontWeight: 500,
        width,
      }}
    >
      {children}
    </th>
  );
}

function TD({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <td
      style={{
        padding: '14px 20px',
        textAlign: align,
      }}
    >
      {children}
    </td>
  );
}
